/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import BeenhereIcon from "@mui/icons-material/Beenhere";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import AlarmOffIcon from "@mui/icons-material/AlarmOff";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

import { theme } from "../../../theme";
import { showNotification } from "../../../common/notifications/notifications";
import {
    approveAnotherDate,
    approvedFtmRequest,
    markRequestIsUndecidede,
    rejectFtmRequest,
} from "../service";
import ModalTemplate from "../../../common/modal/modalTemplate";
import { fetchRemoteConfigData } from "../../../firebase";

const CONFIG_DATA_TYPE = "FTM_REQUEST_APPROVED_FOR_ANOTHER_DATE";

const LaterVisitModal = ({ show, close, job_id, request_id, reloadPage, closeOffcanvers }) => {
    const [notifications, setNotifications] = useState({});

    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);

    const handleClose = () => {
        setDescriptionError(false);
        close();
    };

    useEffect(() => {
        const loadNotificationData = async () => {
            const notification = await fetchRemoteConfigData(CONFIG_DATA_TYPE);
            setNotifications(notification || {});
        };

        if (show) {
            loadNotificationData();
        }
    }, [show]);

    const handleApprove = async () => {
        if (!description.trim()) {
            setDescriptionError(true);
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await approveAnotherDate({ job_id, request_id, description });
            if(response?.status === "Success"){
                toast.success(response.message || "Approved for a later visit");
                reloadPage();
                handleClose();
                closeOffcanvers();
            }else{
                toast.error("Something went wrong");
                return;
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            size={null}
            centered
            title={null}
            body={
                <div className="card-body p-3 text-center">
                    <WarningIcon
                        sx={{ color: theme.main_code, fontSize: 82 }}
                        aria-label="Warning"
                    />
                    <h3 className="mt-3 mb-4 text-dark">{notifications.title}</h3>
                    <p className="mb-4 text-md text-dark">{notifications.text || "Unless previously discussed, discuss with the engineer for the date and time availability prior to the booking."}</p>
                    <textarea
                        className="form-control"
                        rows="5"
                        style={{
                            display: "block",
                            width: "100%",
                            minHeight: "150px",
                            padding: "0.75rem",
                            backgroundColor: "#fff",
                            border: descriptionError ? "1px solid #f5365c" : "1px solid #d2d6da",
                            borderRadius: "0.375rem",
                            boxSizing: "border-box",
                        }}
                        value={description}
                        onChange={(event) => {
                            setDescription(event.target.value);
                            if (descriptionError) setDescriptionError(false);
                        }}
                        aria-label="Availability notes"
                    />
                    {descriptionError && (
                        <div className="mx-n4 mt-3 d-flex align-items-center justify-content-center gap-2 bg-gray-100 py-2">
                            <ErrorRoundedIcon sx={{ color: "#f5365c", fontSize: 24 }} />
                            <span className="text-sm text-secondary">Please enter a description</span>
                        </div>
                    )}
                    <div className="d-flex justify-content-center gap-2 mt-4">
                        <button
                            type="button"
                            className={`btn btn-${theme.main} mb-0 w-100 btn-sm`}
                            onClick={handleApprove}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Saving..." : "Yes"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark mb-0 w-100 btn-sm"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            No
                        </button>
                    </div>
                </div>
            }
        />
    );
}

const OptionMenu = ({
    job_id,
    request_id,
    closeView,
    isShowUndecidedBtn,
    reloadPage = () => { },
}) => {
    const [showLaterVisit, setShowLaterVisit] = useState(false);

    const handleOpenLaterVisit = () => setShowLaterVisit(true);
    const handleCloseLaterVisit = () => setShowLaterVisit(false);

    // ✅ Common result handler (reused everywhere)
    const handleResult = (result, shouldCloseView = true) => {
        if (result.isConfirmed && result.value?.status === "Success") {
            reloadPage();
            toast.success(result.value.message);
        } else {
            toast.error(result.value?.message || "Something went wrong");
        }

        if (shouldCloseView) closeView();
    };

    // ✅ Generic handler (reusable for all actions)
    const handleAction = useCallback(
        ({ notificationType, apiCall, requireInput = false, closeAfter = true }) => {
            showNotification({
                notificationType,
                preConfirm: async (input) => {
                    if (requireInput && !input) {
                        Swal.showValidationMessage("Please enter a description");
                        return;
                    }
                    return await apiCall(input);
                },
                isConfirmed: (result) => handleResult(result, closeAfter),
            });
        },
        [job_id, request_id]
    );

    // ✅ Menu config (clean & maintainable)
    const menuItems = [
        {
            label: "Approve and continue work",
            icon: <BeenhereIcon sx={{ color: theme.main_code }} />,
            fontColor: theme.main_code,
            action: () =>
                handleAction({
                    notificationType: "FTM_REQUEST_APPROVE_IMMEDIATELY",
                    apiCall: () => approvedFtmRequest({
                        job_id,
                        ftm_request_id: request_id,
                    }),
                }),
            primary: true,
        },
        {
            label: "Approve for a later visit",
            icon: <EditCalendarIcon sx={{ color: theme.main_code }} />,
            fontColor: theme.main_code,
            action: handleOpenLaterVisit,
        },
        ...(isShowUndecidedBtn
            ? [
                {
                    label: "Decide later",
                    icon: <AlarmOffIcon sx={{ color: theme.close_btn_code }} />,
                    fontColor: theme.close_btn_code,
                    action: () => handleAction({
                        notificationType: "FTM_REQUEST_UNDECIDED",
                        apiCall: () => markRequestIsUndecidede({ request_id }),
                    }),
                },
            ]
            : []),
        {
            label: "Decline and stop work",
            icon: <ThumbDownIcon sx={{ color: theme.cancel_btn_code }} />,
            fontColor: theme.cancel_btn_code,
            action: () => handleAction({
                notificationType: "FTM_REQUEST_APPROVE_IMMEDIATELY",
                apiCall: () => rejectFtmRequest({
                    job_id,
                    ftm_request_id: request_id,
                }),
            }),
        },
    ];

    return (
        <div style={{ width: "100%" }}>
            {/* ✅ Top Buttons */}
            {menuItems.map((item, index) =>
                item.primary ? (
                    <div key={index}>
                        <button
                            type="button"
                            className={`btn btn-${theme.main} w-100 btn-sm mt-2 mb-0`}
                            onClick={item.action}
                        >
                            {item.label}
                        </button>
                        <p className="mt-1 text-xs mx-2">
                            No payment is taken now. Your card is only pre-authorized
                        </p>
                    </div>
                ) : (
                    <button
                        key={index}
                        className="btn btn-link w-100 text-secondary mb-0"
                        type="button"
                        onClick={item.action}
                    >
                        {item.label}
                    </button>
                )
            )}
            <LaterVisitModal
                show={showLaterVisit}
                close={handleCloseLaterVisit}
                job_id={job_id}
                request_id={request_id}
                reloadPage={reloadPage}
                closeOffcanvers={closeView}
            />
        </div>
    );
};

export default OptionMenu;
